import React, { useState, useEffect } from 'react';
import { 
  Row, 
  Col, 
  Card, 
  Button, 
  Modal, 
  Form, 
  Input, 
  Space, 
  Typography, 
  Tag, 
  Upload, 
  Spin, 
  message as antMessage, 
  Image,
  Dropdown
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  CameraOutlined,
  UploadOutlined,
  CloseOutlined,
  MoreOutlined,
  StopOutlined,
  CheckOutlined
} from '@ant-design/icons';
import fundoNovagGeracao from '../images/FUNDO_NOVAGERACAO_SEMPRE.png';
import { formatarPreco } from '../utils/formatters';
import { fileToBase64, getImagemProduto } from '../utils/imageUtils';
import { buildApiUrl } from '../utils/api';

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;

const AdminProdutos = () => {
  const [produtos, setProdutos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingProduto, setEditingProduto] = useState(null);
  const [form] = Form.useForm();
  const [novoTamanho, setNovoTamanho] = useState('');
  const [tamanhos, setTamanhos] = useState([]);
  const [previewImage, setPreviewImage] = useState(null);
  const [imagemRemovida, setImagemRemovida] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [imagemFile, setImagemFile] = useState(null);
  const [guiaTamanhoFile, setGuiaTamanhoFile] = useState(null);

  useEffect(() => {
    fetchProdutos();
  }, []);

  const fetchProdutos = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(buildApiUrl('/api/admin/produtos'), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setProdutos(data.produtos || []);
      } else {
        antMessage.error('Erro ao carregar produtos');
      }
    } catch (error) {
      console.error('Erro ao buscar produtos:', error);
      antMessage.error('Erro ao carregar produtos');
    } finally {
      setLoading(false);
    }
  };

  const adicionarTamanho = () => {
    if (novoTamanho.trim() && !tamanhos.includes(novoTamanho.trim())) {
      setTamanhos([...tamanhos, novoTamanho.trim()]);
      setNovoTamanho('');
    }
  };

  const removerTamanho = (tamanhoParaRemover) => {
    setTamanhos(tamanhos.filter(tamanho => tamanho !== tamanhoParaRemover));
  };

  const resetForm = () => {
    form.resetFields();
    setNovoTamanho('');
    setTamanhos([]);
    setPreviewImage(null);
    setImagemRemovida(false);
    setEditingProduto(null);
    setShowForm(false);
    setImagemFile(null);
    setGuiaTamanhoFile(null);
  };

  const handleSubmit = async (values) => {
    setUploading(true);

    try {
      const token = localStorage.getItem('token');
      
      // Preparar dados do produto
      const produtoData = {
        nome: values.nome,
        descricao: values.descricao,
        preco: parseFloat(values.preco),
        tamanhos: tamanhos
      };

      // Se há uma nova imagem, converter para base64
      if (imagemFile) {
        produtoData.imagem_base64 = await fileToBase64(imagemFile);
      }
      
      // Se há um novo guia de tamanho, converter para base64
      if (guiaTamanhoFile) {
        produtoData.guia_tamanho_base64 = await fileToBase64(guiaTamanhoFile);
      }
      
      // Se estamos editando e a imagem foi removida, enviar flag para remoção
      if (editingProduto && imagemRemovida && !imagemFile) {
        produtoData.remover_imagem = true;
      }

      const url = editingProduto 
        ? buildApiUrl(`/api/admin/produtos/${editingProduto.id}`)
        : buildApiUrl('/api/admin/produtos');
      
      const method = editingProduto ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(produtoData)
      });

      if (response.ok) {
        await response.json();
        antMessage.success(editingProduto ? 'Produto atualizado com sucesso!' : 'Produto criado com sucesso!');
        resetForm();
        fetchProdutos();
      } else {
        const errorData = await response.json();
        antMessage.error(errorData.message || 'Erro ao salvar produto');
      }
    } catch (error) {
      console.error('Erro ao salvar produto:', error);
      antMessage.error('Erro ao salvar produto');
    } finally {
      setUploading(false);
    }
  };

  const handleEdit = (produto) => {
    setEditingProduto(produto);
    form.setFieldsValue({
      nome: produto.nome,
      descricao: produto.descricao || '',
      preco: produto.preco
    });
    setTamanhos(produto.tamanhos || []);
    
    // Mostrar imagem atual (priorizar base64 sobre URL)
    if (produto.imagem_base64) {
      setPreviewImage(produto.imagem_base64);
    } else if (produto.imagem_url) {
      setPreviewImage(produto.imagem_url);
    } else {
      setPreviewImage(null);
    }
    
    setImagemRemovida(false);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(buildApiUrl(`/api/admin/produtos/${id}`), {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        antMessage.success('Produto deletado permanentemente com sucesso!');
        fetchProdutos();
      } else {
        const errorData = await response.json();
        antMessage.error(errorData.message || 'Erro ao deletar produto');
      }
    } catch (error) {
      antMessage.error('Erro ao remover produto');
    }
  };

  const toggleProdutoStatus = async (produto) => {
    try {
      const token = localStorage.getItem('token');
      
      const produtoData = {
        nome: produto.nome,
        descricao: produto.descricao || '',
        preco: produto.preco,
        tamanhos: produto.tamanhos || [],
        ativo: !produto.ativo
      };

      const response = await fetch(buildApiUrl(`/api/admin/produtos/${produto.id}`), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(produtoData)
      });

      if (response.ok) {
        antMessage.success(`Produto ${produto.ativo ? 'desativado' : 'ativado'} com sucesso!`);
        fetchProdutos();
      } else {
        const errorData = await response.json();
        antMessage.error(errorData.message || 'Erro ao alterar status do produto');
      }
    } catch (error) {
      antMessage.error('Erro ao alterar status do produto');
    }
  };

  const handleImageUpload = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreviewImage(e.target.result);
      setImagemFile(file);
      setImagemRemovida(false);
    };
    reader.readAsDataURL(file);
    return false; // Previne upload automático
  };

  const handleGuiaTamanhoUpload = (file) => {
    setGuiaTamanhoFile(file);
    return false;
  };

  const removeImage = () => {
    setPreviewImage(null);
    setImagemFile(null);
    setImagemRemovida(true);
  };

  if (loading) {
    return (
      <Row justify="center" align="middle" style={{ minHeight: '50vh' }}>
        <Col>
          <Space direction="vertical" align="center">
            <Spin size="large" />
            <Text>Carregando produtos...</Text>
          </Space>
        </Col>
      </Row>
    );
  }

  return (
    <Space direction="vertical" size={24} style={{ width: '100%', padding: '24px', background: '#fff', minHeight: '100vh' }}>
      {/* Header com Background */}
      <Card
        bordered={false}
        bodyStyle={{
          padding: 0,
          background: `linear-gradient(135deg, rgba(62, 121, 247, 0.95) 0%, rgba(30, 90, 220, 0.95) 100%)`,
          backgroundImage: `url(${fundoNovagGeracao})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundBlendMode: 'overlay',
          borderRadius: '8px',
          minHeight: '140px',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <Row justify="center" align="middle" style={{ width: '100%', padding: '40px 40px' }}>
          <Col style={{ textAlign: 'center' }}>
            <Space direction="vertical" size={12}>
              <Title level={1} style={{ color: '#fff', margin: 0, fontWeight: 700, fontSize: '42px' }}>
                Gerenciamento de Produtos
              </Title>
              <Paragraph style={{ color: 'rgba(255, 255, 255, 0.95)', margin: 0, fontSize: '20px', fontWeight: 400 }}>
                Adicione, edite e gerencie todos os produtos da loja
              </Paragraph>
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Botão Adicionar Produto */}
      <Row justify="end">
        <Col>
          <Button
            type="primary"
            size="large"
            icon={<PlusOutlined />}
            onClick={() => setShowForm(true)}
          >
            Adicionar Novo Produto
          </Button>
        </Col>
      </Row>

      {/* Modal de Formulário */}
      <Modal
        title={editingProduto ? 'Editar Produto' : 'Novo Produto'}
        open={showForm}
        onCancel={resetForm}
        footer={null}
        width={700}
        destroyOnClose
        wrapClassName="admin-produtos-modal"
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          style={{ marginTop: 24 }}
        >
          <Form.Item
            label="Nome do Produto"
            name="nome"
            rules={[{ required: true, message: 'Por favor, insira o nome do produto' }]}
          >
                <input
                  type="text"
              className="filter-input"
                  placeholder="Digite o nome do produto"
              value={form.getFieldValue('nome') || ''}
              onChange={(e) => form.setFieldValue('nome', e.target.value)}
            />
          </Form.Item>

          <Form.Item
            label="Descrição"
            name="descricao"
            rules={[{ required: true, message: 'Por favor, insira a descrição' }]}
          >
            <TextArea 
              placeholder="Digite a descrição do produto"
              rows={6}
              className="filter-input"
            />
          </Form.Item>

          <Form.Item
            label="Preço (R$)"
            name="preco"
            rules={[{ required: true, message: 'Por favor, insira o preço' }]}
          >
                  <input
                    type="number"
              className="filter-input"
              placeholder="0.00"
                    step="0.01"
                    min="0"
              value={form.getFieldValue('preco') || ''}
              onChange={(e) => form.setFieldValue('preco', e.target.value)}
            />
          </Form.Item>

          <Form.Item label="Guia de Tamanho (Opcional)">
            <Upload
              beforeUpload={handleGuiaTamanhoUpload}
              maxCount={1}
                    accept="image/*"
            >
              <Button 
                icon={<UploadOutlined />} 
                size="large"
                style={{
                  border: '2px solid #d9d9d9',
                  borderRadius: '6px',
                  height: '40px'
                }}
              >
                    Selecionar Guia de Tamanho
              </Button>
            </Upload>
            {guiaTamanhoFile && (
              <Text type="success" style={{ marginTop: 8, display: 'block' }}>
                <CheckCircleOutlined /> {guiaTamanhoFile.name}
              </Text>
            )}
          </Form.Item>

          <Form.Item label="Tamanhos Disponíveis">
            <div style={{ display: 'flex', gap: '8px' }}>
                    <input
                      type="text"
                className="filter-input"
                placeholder="Ex: M, 38, G, 40..."
                      value={novoTamanho}
                      onChange={(e) => setNovoTamanho(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          adicionarTamanho();
                        }
                      }}
                style={{ flex: 1 }}
                    />
              <Button 
                type="primary" 
                      onClick={adicionarTamanho}
                size="large"
                icon={<PlusOutlined />}
                    >
                Adicionar
              </Button>
                  </div>
            {tamanhos.length > 0 && (
              <Space wrap style={{ marginTop: 12 }}>
                {tamanhos.map((tamanho, index) => (
                  <Tag
                    key={index}
                    closable
                    onClose={() => removerTamanho(tamanho)}
                    style={{ padding: '4px 12px', fontSize: 14 }}
                  >
                          {tamanho}
                  </Tag>
                ))}
              </Space>
            )}
          </Form.Item>

          <Form.Item label="Imagem do Produto (Opcional)">
            <Upload
              beforeUpload={handleImageUpload}
              maxCount={1}
              accept="image/*"
              showUploadList={false}
            >
              <Button 
                icon={<CameraOutlined />} 
                size="large"
                style={{
                  border: '2px solid #d9d9d9',
                  borderRadius: '6px',
                  height: '40px'
                }}
              >
                Selecionar Imagem
              </Button>
            </Upload>
            {previewImage && (
              <div style={{ marginTop: 16, textAlign: 'center' }}>
                <Image
                  src={previewImage}
                  alt="Preview"
                  style={{ maxWidth: 250, maxHeight: 250, borderRadius: 8 }}
                />
                <br />
                <Button
                  danger
                  onClick={removeImage}
                  style={{ marginTop: 12 }}
                  icon={<CloseOutlined />}
                >
                  Remover Imagem
                </Button>
        </div>
      )}
          </Form.Item>

          <Form.Item style={{ marginTop: 24, marginBottom: 0 }}>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button size="large" onClick={resetForm}>
                Cancelar
              </Button>
              <Button type="primary" htmlType="submit" size="large" loading={uploading}>
                {editingProduto ? 'Atualizar' : 'Criar'} Produto
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Grid de Produtos */}
      {produtos.length === 0 ? (
        <Card style={{ textAlign: 'center', padding: '60px 20px' }}>
          <Space direction="vertical" size="large">
            <Text style={{ fontSize: 18, color: '#666' }}>
              Nenhum produto encontrado.
            </Text>
            <Button
              type="primary"
              size="large"
              icon={<PlusOutlined />}
              onClick={() => setShowForm(true)}
            >
              Adicionar Primeiro Produto
            </Button>
          </Space>
        </Card>
      ) : (
        <Row gutter={[24, 24]}>
          {produtos.map(produto => (
            <Col xs={24} sm={12} lg={8} xl={6} key={produto.id}>
              <Card
                hoverable
                styles={{ 
                  body: { padding: 0 }
                }}
                style={{ 
                  opacity: produto.ativo ? 1 : 0.7,
                  filter: produto.ativo ? 'none' : 'grayscale(30%)',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden'
                }}
                cover={
                  <div style={{ 
                    height: 200, 
                    background: '#f8f9fa', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    position: 'relative',
                    overflow: 'hidden'
                  }}>
                    {getImagemProduto(produto) ? (
                      <img 
                        src={getImagemProduto(produto)} 
                        alt={produto.nome}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      <CameraOutlined style={{ fontSize: 48, color: '#ccc' }} />
                    )}
                    <div style={{
                      position: 'absolute',
                      top: 10,
                      left: 10,
                      zIndex: 10
                    }}>
                      <Dropdown
                        menu={{
                          items: [
                            {
                              key: 'edit',
                              label: 'Editar',
                              icon: <EditOutlined />,
                              onClick: () => handleEdit(produto)
                            },
                            {
                              key: 'toggle',
                              label: produto.ativo ? 'Desativar' : 'Ativar',
                              icon: produto.ativo ? <StopOutlined /> : <CheckOutlined />,
                              onClick: () => toggleProdutoStatus(produto)
                            },
                            {
                              type: 'divider'
                            },
                            {
                              key: 'delete',
                              label: 'Remover',
                              icon: <DeleteOutlined />,
                              danger: true,
                              onClick: () => {
                                Modal.confirm({
                                  title: 'Deletar Produto',
                                  content: 'ATENÇÃO: Esta ação é irreversível! Tem certeza que deseja deletar permanentemente este produto?',
                                  okText: 'Sim, deletar',
                                  cancelText: 'Cancelar',
                                  okButtonProps: { danger: true },
                                  onOk: () => handleDelete(produto.id)
                                });
                              }
                            }
                          ]
                        }}
                        trigger={['click']}
                      >
                        <Button 
                          type="text" 
                          icon={<MoreOutlined />}
                          style={{ 
                            fontSize: 18,
                            width: 32,
                            height: 32,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: 'rgba(0, 0, 0, 0.6)',
                            color: 'white',
                            borderRadius: '50%',
                            backdropFilter: 'blur(4px)'
                          }}
                        />
                      </Dropdown>
                    </div>
                    <div style={{
                      position: 'absolute',
                      top: 10,
                      right: 10,
                      background: 'rgba(0, 0, 0, 0.8)',
                      color: 'white',
                      padding: '4px 12px',
                      borderRadius: 20,
                      fontSize: 12,
                      fontWeight: 600
                    }}>
                      {produto.ativo ? (
                        <><CheckCircleOutlined /> Ativo</>
                      ) : (
                        <><CloseCircleOutlined /> Inativo</>
                      )}
                    </div>
                  </div>
                }
              >
                <div style={{ 
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between'
                }}>
                  <div style={{ padding: '16px' }}>
                    <Card.Meta
                      title={
                        <div style={{ 
                          height: '56px',
                          overflow: 'hidden',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical'
                        }}>
                          {produto.nome}
                        </div>
                      }
                      description={
                        <Paragraph 
                          ellipsis={{ rows: 2 }}
                          style={{ 
                            color: '#666', 
                            marginBottom: 16,
                            height: '48px',
                            overflow: 'hidden'
                          }}
                        >
                          {produto.descricao}
                        </Paragraph>
                      }
                    />
                    <Space direction="vertical" size="small" style={{ width: '100%', marginTop: 12 }}>
                      <Text strong style={{ fontSize: 18, color: '#024638' }}>
                        {formatarPreco(produto.preco)}
                      </Text>
                      {produto.tamanhos && produto.tamanhos.length > 0 ? (
                        <div style={{ minHeight: '60px' }}>
                          <Text type="secondary" style={{ fontSize: 12, fontWeight: 600 }}>
                            TAMANHOS:
                          </Text>
                          <div style={{ marginTop: 4 }}>
                            <Space wrap size={4}>
                              {produto.tamanhos.map((tamanho, index) => (
                                <Tag key={index} color="gold">
                                  {tamanho}
                                </Tag>
                              ))}
                            </Space>
                          </div>
                        </div>
                      ) : (
                        <div style={{ minHeight: '60px' }} />
                      )}
                    </Space>
                  </div>
                </div>
              </Card>
            </Col>
          ))}
        </Row>
      )}
    </Space>
  );
};

export default AdminProdutos; 
